import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, ButtonGroup, Card, Col, Form, InputGroup } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { setHours, setMinutes } from 'date-fns';
import { NumericFormat } from 'react-number-format';

import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import DatePicker from '@components/Common/AwpDatePicker';
import { WeatherStationDropdown } from '@components/Common/WeatherStationDropdown';

import { DATA_RANGE } from '@awp/constants';
import { debounce } from '@awp/helpers/Utils';

import { useFrostProbeContext } from './FrostProbeContext';
import ObservationTimeDropdown from '../Common/ObservationTime';

/**
 * A component for configuring data filters including weather station selection, and date range selection.
 * @returns {JSX.Element} The data filter form.
 */
const DataFilters = () => {
  const dispatch = useDispatch();

  const { dataRange, setEndDate, setObservationTime } = useFrostProbeContext();

  /**
   * Updates the end date in the data range when a new date is selected.
   * @param {string|Date} newEndDate - The new end date selected from the date picker.
   */
  const dispatchDateChanges = (newEndDate) => {
    let todayDate = new Date();
    const endDateEpoch =
      new Date(newEndDate).toDateString() == todayDate.toDateString()
        ? todayDate.getTime()
        : setMinutes(setHours(new Date(newEndDate), 23), 59).getTime();

    setEndDate(endDateEpoch);
  };

  // Debounce the dispatchDateChanges or any other logic in handleChange
  const handleChange = useCallback(
    debounce((val) => {
      dispatchDateChanges(val);
    }, 1000),
    [dispatchDateChanges],
  ); // Dependency array ensures stability if dispatchDateChanges changes

  const SetObservationTime = (obsTime) => async () => {
    setObservationTime(obsTime);
  };

  return (
    <>
      <Accordion defaultActiveKey="0">
        <Card border="light" className="page-card">
          <CardHeaderToggle eventKey="0" as="h3" title={'Station & Date Selection'} />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div>
                <Form>
                  <Form.Row>
                    <Col lg={5} md={6} sm={6} className="mb-4" style={{ zIndex: 30 }}>
                      <WeatherStationDropdown zIndex={'1000'} sensorTypeDisabled={true} />
                    </Col>
                    <Col lg={2} md={6} sm={6} className="mb-3" style={{ zIndex: 20 }}>
                      <ObservationTimeDropdown ObservationTimeChanged={SetObservationTime} />
                    </Col>
                    <Col lg={2} md={6} sm={6} className="mb-2">
                      <div>
                        <Form.Label>
                          <b>End Date</b>
                        </Form.Label>
                        <div style={{ width: '100%' }}>
                          <DatePicker
                            elementState={{
                              value: dataRange.endDate,
                              error: '',
                              disabled: false,
                            }}
                            className="form-control input-sm"
                            dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                            handleChange={handleChange}
                            showTime={{ isEnabled: false }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={6} style={{ zIndex: 10 }}>
                      <AwpDisplayRange />
                    </Col>
                  </Form.Row>
                </Form>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  );
};

const displayRangeOptions = [
  { label: '4 Days', value: 4 },
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: '28 Days', value: 28 },
  { label: 'Custom', value: DATA_RANGE.CUSTOM }, //the select doesn't set the item when it's -1
];

const getDisplayRangeOption = (value) => {
  let option = displayRangeOptions.filter((o) => o.value === value);
  if (option.length === 0) {
    option = displayRangeOptions.filter((o) => o.value === DATA_RANGE.CUSTOM);
  }

  return option;
};

/**
 * Component for selecting the display range of the data.
 * @returns {JSX.Element} A component allowing users to select how much data to display.
 */
const AwpDisplayRange = () => {
  const { dataRange, updateDataRange } = useFrostProbeContext();

  const [isCustomDataRange, setIsCustomDataRange] = useState(false);
  const [selectedValue, setSelectedValue] = useState(getDisplayRangeOption(dataRange.displayRange));

  useEffect(() => {
    const dataRangeOption = getDisplayRangeOption(dataRange.displayRange);
    setSelectedValue(dataRangeOption);
    setIsCustomDataRange(dataRangeOption[0].value === DATA_RANGE.CUSTOM);
  }, [dataRange]);

  /**
   * Dispatches changes to the display range state.
   * @param {number} newDataRange - The new display range value.
   */
  const dispatchDisplayRangeChanges = (newDataRange) => {
    if (!isNaN(newDataRange)) {
      updateDataRange(dataRange.endDate, newDataRange);
    }
  };

  /**
   * Handles changes to the display range selection.
   * @param {number} value - The selected display range value.
   */
  const dataRangeChange = (value) => {
    setSelectedValue(getDisplayRangeOption(value));

    if (value === DATA_RANGE.CUSTOM) {
      setIsCustomDataRange(true);
    } else {
      setIsCustomDataRange(false);
      dispatchDisplayRangeChanges(value);
    }
  };

  return (
    <>
      <Form.Label>
        <b>Display Range</b>
      </Form.Label>
      <InputGroup className="mb-3" style={{ zIndex: '1000' }}>
        <Select
          className="data-range-select"
          options={displayRangeOptions}
          value={selectedValue}
          onChange={(e) => {
            dataRangeChange(e.value);
          }}
        />
        <ButtonGroup className="data-range-input">
          <NumericFormat
            isAllowed={(values) => {
              const { floatValue } = values;

              return floatValue === undefined || (floatValue <= DATA_RANGE.MAXIMUM && floatValue >= DATA_RANGE.MINIMUM);
            }}
            onKeyUp={(e) => {
              e.persist();
              setTimeout(() => {
                if (e.target != null) dispatchDisplayRangeChanges(parseInt(e.target.value));
              }, 1500);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            allowNegative={false}
            decimalScale={0}
            disabled={!isCustomDataRange}
            valueIsNumericString={true}
            className="form-control input-sm data-range-custom"
            thousandSeparator={true}
            value={dataRange.displayRange}
          />
        </ButtonGroup>
      </InputGroup>
    </>
  );
};

export default DataFilters;
